import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionBlocksEditor from '@/components/estimates/SectionBlocksEditor';
import { getLegalPack, resolveLegalState } from '@/lib/legalPacks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Trash2,
  Layers,
  ChevronDown,
  ChevronRight,
  Wallet,
  Package,
  Lock,
  Info,
  ClipboardList,
  MapPin,
  Calendar as CalendarIcon,
  AlertTriangle,
  FileSignature,
  LayoutList,
  AlignLeft,
  Clock,
} from 'lucide-react';
import {
  formatUsd,
  sectionItemTotal,
  computeSectionTotals,
} from '@/lib/estimatesData';
import { translatePhase } from '@/lib/i18nLabels';

// Canonical preset keys; rendered via translatePhase / estimates.section.* so the
// dropdown reflects the active language but the stored section name remains the
// user's chosen label.
const SECTION_PRESET_KEYS = [
  'lead',
  'demo',
  'framing',
  'rough_ins',
  'drywall',
  'paint',
  'trim',
  'punch_list',
];

const HelpDot = ({ text, label }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center h-4 w-4 rounded-full text-muted-foreground hover:text-foreground"
          aria-label={label}
        >
          <Info size={11} />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="max-w-xs text-xs leading-snug bg-[hsl(var(--card))] border border-border text-foreground"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/* ==========================================================================
 * Main scope line item (with HOP checkbox)
 * ======================================================================= */
const LineItemRow = ({ section, item, onUpdate, onRemove }) => {
  const { t } = useTranslation();
  const total = sectionItemTotal(item);
  const qtyDisplay = !item.qty ? '' : String(item.qty);
  const rateDisplay = !item.rate ? '' : String(item.rate);
  return (
    <div
      className="grid grid-cols-12 items-center gap-2 py-1"
      data-testid={`line-item-${item.id}`}
    >
      <Input
        value={item.desc || ''}
        onChange={(e) => onUpdate(section.id, item.id, { desc: e.target.value })}
        placeholder={t('estimates.builder.desc_placeholder')}
        className="col-span-12 sm:col-span-4 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
        data-testid={`line-item-desc-${item.id}`}
      />
      <Input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.5"
        value={qtyDisplay}
        onChange={(e) =>
          onUpdate(section.id, item.id, { qty: Number(e.target.value) || 0 })
        }
        placeholder="0"
        className="col-span-3 sm:col-span-1 h-9 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/50"
        data-testid={`line-item-qty-${item.id}`}
      />
      <Input
        value={item.unit || 'ea'}
        onChange={(e) => onUpdate(section.id, item.id, { unit: e.target.value })}
        placeholder={t('estimates.builder.unit_placeholder')}
        className="col-span-3 sm:col-span-1 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
        data-testid={`line-item-unit-${item.id}`}
      />
      {/* No min: a credit line ("Less construction credit") is a negative
          rate, and it has to stay editable like any other line. */}
      <Input
        type="number"
        inputMode="decimal"
        step="1"
        value={rateDisplay}
        onChange={(e) =>
          onUpdate(section.id, item.id, { rate: Number(e.target.value) || 0 })
        }
        placeholder="0.00"
        className="col-span-4 sm:col-span-2 h-9 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/50"
        data-testid={`line-item-rate-${item.id}`}
      />
      <div
        className="col-span-2 sm:col-span-2 text-right text-sm font-semibold tabular-nums"
        data-testid={`line-item-total-${item.id}`}
      >
        {total > 0 ? (
          <span className="text-foreground">{formatUsd(total)}</span>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </div>

      {/* HOP checkbox + remove */}
      <div className="col-span-10 sm:col-span-1 flex items-center gap-1.5">
        <Checkbox
          id={`line-item-hop-${item.id}`}
          checked={!!item.ho_pick}
          onCheckedChange={(v) =>
            onUpdate(section.id, item.id, { ho_pick: !!v })
          }
          className="border-border data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
          data-testid={`line-item-hop-${item.id}`}
        />
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <label
                htmlFor={`line-item-hop-${item.id}`}
                className={`text-[10px] uppercase tracking-[0.16em] cursor-pointer select-none ${
                  item.ho_pick ? 'text-blue-700 dark:text-blue-300' : 'text-muted-foreground'
                }`}
              >
                {t('estimates.builder.col_hop')}
              </label>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="end"
              className="max-w-xs text-xs leading-snug bg-[hsl(var(--card))] border border-border text-foreground"
            >
              {t('estimates.builder.hop_tooltip')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="col-span-2 sm:col-span-1 flex justify-end">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRemove(section.id, item.id)}
          className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-7 w-7"
          aria-label={t('estimates.builder.remove_line')}
          data-testid={`line-item-remove-${item.id}`}
        >
          <Trash2 size={12} />
        </Button>
      </div>
    </div>
  );
};

/* ==========================================================================
 * Simple sub-row (used for Materials and Allowances) — no HOP, no flags
 * ======================================================================= */
const SubRow = ({ row, onUpdate, onRemove, prefix }) => {
  const { t } = useTranslation();
  const total = sectionItemTotal(row);
  const qtyDisplay = !row.qty ? '' : String(row.qty);
  const rateDisplay = !row.rate ? '' : String(row.rate);
  return (
    <div
      className="grid grid-cols-12 items-center gap-2 py-1"
      data-testid={`${prefix}-row-${row.id}`}
    >
      <Input
        value={row.desc || ''}
        onChange={(e) => onUpdate(row.id, { desc: e.target.value })}
        placeholder={t('estimates.builder.desc_placeholder')}
        className="col-span-12 sm:col-span-5 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
        data-testid={`${prefix}-row-desc-${row.id}`}
      />
      <Input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.5"
        value={qtyDisplay}
        onChange={(e) => onUpdate(row.id, { qty: Number(e.target.value) || 0 })}
        placeholder="0"
        className="col-span-3 sm:col-span-1 h-9 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/50"
        data-testid={`${prefix}-row-qty-${row.id}`}
      />
      <Input
        value={row.unit || 'ea'}
        onChange={(e) => onUpdate(row.id, { unit: e.target.value })}
        placeholder={t('estimates.builder.unit_placeholder')}
        className="col-span-3 sm:col-span-1 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
        data-testid={`${prefix}-row-unit-${row.id}`}
      />
      <Input
        type="number"
        inputMode="decimal"
        min="0"
        step="1"
        value={rateDisplay}
        onChange={(e) => onUpdate(row.id, { rate: Number(e.target.value) || 0 })}
        placeholder="0.00"
        className="col-span-4 sm:col-span-2 h-9 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/50"
        data-testid={`${prefix}-row-rate-${row.id}`}
      />
      <div
        className="col-span-2 sm:col-span-2 text-right text-sm font-semibold tabular-nums"
        data-testid={`${prefix}-row-total-${row.id}`}
      >
        {total > 0 ? (
          <span className="text-foreground">{formatUsd(total)}</span>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </div>
      <div className="col-span-12 sm:col-span-1 flex justify-end">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRemove(row.id)}
          className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-7 w-7"
          aria-label={t('estimates.builder.remove_row')}
          data-testid={`${prefix}-row-remove-${row.id}`}
        >
          <Trash2 size={12} />
        </Button>
      </div>
    </div>
  );
};

/* ==========================================================================
 * Collapsible sub-section (Materials or Allowances)
 * ======================================================================= */
const CollapsibleSubSection = ({
  sectionId,
  title,
  // lowercased topic word used inside the empty/notes copy (e.g. "materiales")
  topic,
  icon: Icon,
  accentClass,
  rows = [],
  notes = '',
  rowPrefix,
  rowsTotal,
  description,
  defaultOpen = false,
  onAddRow,
  onUpdateRow,
  onRemoveRow,
  onUpdateNotes,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen || rows.length > 0 || !!notes);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={`rounded-md border ${accentClass}`}
      data-testid={`${rowPrefix}-collapsible-${sectionId}`}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-foreground/5 transition-colors rounded-t-md"
          data-testid={`${rowPrefix}-toggle-${sectionId}`}
        >
          <div className="flex items-center gap-2">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Icon size={13} />
            <span className="text-xs uppercase tracking-[0.18em] font-semibold">
              {title}
            </span>
            <Badge
              variant="outline"
              className="bg-[hsl(var(--muted))] text-muted-foreground border-border text-[10px] px-1.5 py-0"
            >
              {rows.length}
            </Badge>
          </div>
          <div className="text-sm font-semibold tabular-nums">
            {rowsTotal > 0 ? (
              formatUsd(rowsTotal)
            ) : (
              <span className="text-muted-foreground/40">—</span>
            )}
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 pt-1">
        {description ? (
          <div className="text-[11px] text-muted-foreground mb-2 leading-snug">
            {description}
          </div>
        ) : null}

        <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground px-1">
          <span className="col-span-5">{t('estimates.builder.col_description')}</span>
          <span className="col-span-1">{t('estimates.builder.col_qty')}</span>
          <span className="col-span-1">{t('estimates.builder.col_unit')}</span>
          <span className="col-span-2">{t('estimates.builder.col_price')}</span>
          <span className="col-span-2 text-right">{t('estimates.builder.col_total')}</span>
          <span className="col-span-1">&nbsp;</span>
        </div>
        <div className="mt-1.5 space-y-1">
          {rows.length === 0 ? (
            <div className="text-xs text-muted-foreground py-3 text-center">
              {t('estimates.builder.no_rows_yet', { topic })}
            </div>
          ) : (
            rows.map((row) => (
              <SubRow
                key={row.id}
                row={row}
                prefix={rowPrefix}
                onUpdate={(rowId, patch) => onUpdateRow(sectionId, rowId, patch)}
                onRemove={(rowId) => onRemoveRow(sectionId, rowId)}
              />
            ))
          )}
        </div>

        <div className="mt-3">
          <Label
            className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
            htmlFor={`${rowPrefix}-notes-${sectionId}`}
          >
            {t('estimates.builder.notes_optional')}
          </Label>
          <Textarea
            id={`${rowPrefix}-notes-${sectionId}`}
            rows={2}
            value={notes || ''}
            onChange={(e) => onUpdateNotes(sectionId, e.target.value)}
            placeholder={t('estimates.builder.notes_about', { topic })}
            className="mt-1.5 bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
            data-testid={`${rowPrefix}-notes-${sectionId}`}
          />
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAddRow(sectionId)}
            className="border-border hover:bg-foreground/5 uppercase tracking-[0.16em] text-xs"
            data-testid={`${rowPrefix}-add-${sectionId}`}
          >
            <Plus size={12} className="mr-1.5" />
            {t('estimates.builder.add_row')}
          </Button>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

/* ==========================================================================
 * Section editor
 * ======================================================================= */
const SectionEditor = ({
  section,
  layout = 'itemized',
  onUpdate,
  onRemove,
  // content blocks (open-doc scope editor)
  onAddBlock,
  onUpdateBlock,
  onRemoveBlock,
  onMoveBlock,
  // items
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  // materials
  onAddMaterial,
  onUpdateMaterial,
  onRemoveMaterial,
  // allowances
  onAddAllowance,
  onUpdateAllowance,
  onRemoveAllowance,
  // bottom-line
  onSetBottomLineMode,
  onSetBottomLineTotal,
}) => {
  const { t } = useTranslation();
  const secTotals = computeSectionTotals(section);
  const isBottomLine = !!section.bottom_line_mode;
  const overrideValue =
    section.bottom_line_total === null || section.bottom_line_total === undefined
      ? ''
      : String(section.bottom_line_total);

  // Translated section preset list — phase labels + Materials. The stored
  // section name is the user-entered string; this list only seeds the
  // autocomplete dropdown.
  const sectionPresets = [
    ...SECTION_PRESET_KEYS.map((k) => translatePhase(t, k)),
    t('estimates.section.materials'),
  ];

  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid={`section-${section.id}`}
    >
      <CardContent className="p-4">
        {/* Section header row: name + remove */}
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-[hsl(var(--primary))]" />
          <Input
            value={section.name || ''}
            onChange={(e) => onUpdate(section.id, { name: e.target.value })}
            placeholder={t('estimates.builder.section_name_placeholder')}
            list={`section-presets-${section.id}`}
            className="h-9 flex-1 bg-[hsl(var(--muted))] border-border text-foreground font-semibold"
            data-testid={`section-name-${section.id}`}
          />
          <datalist id={`section-presets-${section.id}`}>
            {sectionPresets.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onRemove(section.id)}
            className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-7 w-7 shrink-0"
            aria-label={t('estimates.builder.remove_section')}
            data-testid={`section-remove-${section.id}`}
          >
            <Trash2 size={14} />
          </Button>
        </div>

        {/* Section type (scope-centric layout only) — Base scope rolls into
            "Total Base Cost"; Allowance rolls into "Plus Allowances". */}
        {layout === 'scope' ? (
          <div
            className="mt-2.5 flex items-center gap-2 flex-wrap"
            data-testid={`section-kind-${section.id}`}
          >
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Section type
            </span>
            <div className="inline-flex rounded-md border border-border overflow-hidden">
              {[
                { value: 'base', label: 'Base scope' },
                { value: 'allowance', label: 'Allowance' },
              ].map((opt) => {
                const active = (section.kind || 'base') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onUpdate(section.id, { kind: opt.value })}
                    aria-pressed={active}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      active
                        ? 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                        : 'bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`section-kind-${opt.value}-${section.id}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Written scope — the open-doc content blocks (Scope layout only). */}
        {layout === 'scope' ? (
          <div className="mt-4 rounded-md border border-dashed border-border/70 bg-[hsl(var(--muted))]/30 p-3">
            <SectionBlocksEditor
              blocks={section.blocks}
              onAdd={(type) => onAddBlock(section.id, type)}
              onUpdate={(blockId, patch) =>
                onUpdateBlock(section.id, blockId, patch)
              }
              onRemove={(blockId) => onRemoveBlock(section.id, blockId)}
              onMove={(blockId, dir) => onMoveBlock(section.id, blockId, dir)}
            />
          </div>
        ) : null}

        {/* Main scope items */}
        <div className="mt-4">
          <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground px-1">
            <span className="col-span-4">{t('estimates.builder.col_description')}</span>
            <span className="col-span-1">{t('estimates.builder.col_qty')}</span>
            <span className="col-span-1">{t('estimates.builder.col_unit')}</span>
            <span className="col-span-2">{t('estimates.builder.col_rate')}</span>
            <span className="col-span-2 text-right">{t('estimates.builder.col_total')}</span>
            <span className="col-span-1 text-center">{t('estimates.builder.col_hop')}</span>
            <span className="col-span-1">&nbsp;</span>
          </div>
          <div className="mt-1 space-y-1">
            {(section.items || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                {t('estimates.builder.no_lines_yet')}
              </div>
            ) : (
              (section.items || []).map((item) => (
                <LineItemRow
                  key={item.id}
                  section={section}
                  item={item}
                  onUpdate={onUpdateItem}
                  onRemove={onRemoveItem}
                />
              ))
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAddItem(section.id)}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid={`section-add-item-${section.id}`}
            >
              <Plus size={12} className="mr-1.5" />
              {t('estimates.builder.add_line_item')}
            </Button>
          </div>
        </div>

        {/* Materials and Allowances collapsibles */}
        <div className="mt-5 space-y-3">
          <CollapsibleSubSection
            sectionId={section.id}
            title={t('estimates.section.materials')}
            topic={t('estimates.section.materials').toLowerCase()}
            icon={Package}
            accentClass="border-border bg-foreground/[0.02]"
            rows={section.materials || []}
            notes={section.materials_notes}
            rowPrefix="mat"
            rowsTotal={secTotals.section_materials_total}
            description={t('estimates.builder.materials_help')}
            onAddRow={onAddMaterial}
            onUpdateRow={onUpdateMaterial}
            onRemoveRow={onRemoveMaterial}
            onUpdateNotes={(secId, val) =>
              onUpdate(secId, { materials_notes: val })
            }
          />
          <CollapsibleSubSection
            sectionId={section.id}
            title={t('estimates.section.allowances')}
            topic={t('estimates.section.allowances').toLowerCase()}
            icon={Wallet}
            accentClass="border-amber-400/30 bg-amber-500/5"
            rows={section.allowances || []}
            notes={section.allowances_notes}
            rowPrefix="alw"
            rowsTotal={secTotals.section_allowances_total}
            description={t('estimates.builder.allowances_help')}
            onAddRow={onAddAllowance}
            onUpdateRow={onUpdateAllowance}
            onRemoveRow={onRemoveAllowance}
            onUpdateNotes={(secId, val) =>
              onUpdate(secId, { allowances_notes: val })
            }
          />
        </div>

        {/* Section notes */}
        <div className="mt-5">
          <Label
            className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
            htmlFor={`section-notes-${section.id}`}
          >
            {t('estimates.section.notes')}
          </Label>
          <Textarea
            id={`section-notes-${section.id}`}
            rows={2}
            value={section.notes || ''}
            onChange={(e) => onUpdate(section.id, { notes: e.target.value })}
            placeholder={t('estimates.builder.section_notes_placeholder')}
            className="mt-1.5 bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
            data-testid={`section-notes-${section.id}`}
          />
        </div>

        {/* Prominent section total footer banner with Bottom Line toggle */}
        <div
          className={`mt-5 -mx-4 -mb-4 px-4 py-3 border-t rounded-b-md flex items-center justify-between gap-3 flex-wrap ${
            isBottomLine
              ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/15'
              : 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10'
          }`}
          data-testid={`section-total-banner-${section.id}`}
        >
          {/* Left side — Bottom Line toggle */}
          <div className="flex items-center gap-2 min-w-0">
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-foreground/80"
                  >
                    <Lock
                      size={12}
                      className={
                        isBottomLine
                          ? 'text-[hsl(var(--primary))]'
                          : 'text-muted-foreground'
                      }
                    />
                    {t('estimates.section.bottom_line')}
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  className="max-w-xs text-xs leading-snug bg-[hsl(var(--card))] border border-border text-foreground"
                >
                  {t('estimates.builder.bottom_line_tooltip')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch
              checked={isBottomLine}
              onCheckedChange={(v) => onSetBottomLineMode(section.id, !!v)}
              data-testid={`section-bottom-line-toggle-${section.id}`}
              className="data-[state=checked]:bg-[hsl(var(--primary))]"
            />
          </div>

          {/* Right side — Section total or editable override */}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]/90 font-semibold">
              {t('estimates.section.section_total')}
            </span>
            {isBottomLine ? (
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={overrideValue}
                onChange={(e) =>
                  onSetBottomLineTotal(section.id, e.target.value)
                }
                placeholder={String(secTotals.section_computed_total || 0)}
                className="h-10 w-32 sm:w-40 text-right text-xl sm:text-2xl font-bold text-[hsl(var(--primary))] tabular-nums bg-[hsl(var(--muted))] border-[hsl(var(--primary))]/40 focus-visible:ring-[hsl(var(--primary))]/60"
                data-testid={`section-total-override-${section.id}`}
              />
            ) : (
              <span
                className="text-2xl sm:text-3xl font-bold text-[hsl(var(--primary))] tabular-nums leading-none"
                data-testid={`section-total-prominent-${section.id}`}
              >
                {formatUsd(secTotals.section_display_total)}
              </span>
            )}
          </div>
        </div>

        {/* Bottom-line helper note when active */}
        {isBottomLine ? (
          <div className="mt-3 -mb-1 text-[11px] text-[hsl(var(--primary))]/80 leading-snug">
            <Lock size={11} className="inline mr-1 -mt-0.5" />
            {t('estimates.builder.bottom_line_on_helper')}{' '}
            <span className="font-semibold">
              {formatUsd(secTotals.section_computed_total)}
            </span>
            {overrideValue !== '' &&
            Number(overrideValue) !== secTotals.section_computed_total
              ? t('estimates.builder.bottom_line_override')
              : ''}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

/* ==========================================================================
 * Main builder
 * ======================================================================= */
export const EstimateBuilder = ({
  estimate,
  clients = [],
  totals,
  onChange,
  onAddSection,
  onRemoveSection,
  onUpdateSection,
  // content blocks (open-doc scope editor)
  onAddBlock,
  onUpdateBlock,
  onRemoveBlock,
  onMoveBlock,
  // items
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  // materials
  onAddMaterial,
  onUpdateMaterial,
  onRemoveMaterial,
  // allowances
  onAddAllowance,
  onUpdateAllowance,
  onRemoveAllowance,
  // bottom line
  onSetBottomLineMode,
  onSetBottomLineTotal,
  onSetAllSectionsBottomLineMode,
}) => {
  const { t } = useTranslation();
  const setField = (k) => (e) =>
    onChange({ ...estimate, [k]: e?.target?.value ?? '' });

  const allBottomLine = !!totals?.all_bottom_line;
  const anyBottomLine = !!totals?.any_bottom_line;

  return (
    <div className="space-y-4" data-testid="estimate-builder">
      {/* Project name + Client */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t('estimates.builder.project_name')}
            </Label>
            <Input
              value={estimate.project_name || ''}
              onChange={setField('project_name')}
              placeholder={t('estimates.builder.project_name_placeholder')}
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
              data-testid="estimate-project-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t('estimates.builder.client')}
            </Label>
            <Select
              value={estimate.client_id || 'none'}
              onValueChange={(v) => {
                if (v === 'none') {
                  onChange({ ...estimate, client_id: null, client_name: '' });
                } else {
                  const found = clients.find((c) => c.id === v);
                  onChange({
                    ...estimate,
                    client_id: v,
                    client_name: found?.name || '',
                    // Auto-fill the project address from the selected client/lead
                    // when the estimate doesn't already have one (don't clobber
                    // an address the user typed).
                    overview: {
                      ...(estimate.overview || {}),
                      address: estimate?.overview?.address || found?.address || '',
                    },
                  });
                }
              }}
            >
              <SelectTrigger
                className="h-10 bg-[hsl(var(--muted))] border-border"
                data-testid="estimate-client"
              >
                <SelectValue placeholder={t('estimates.builder.select_client')} />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                <SelectItem value="none">
                  {t('estimates.builder.no_client')}
                </SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                    {c.type === 'lead' ? ' (Lead)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Proposal layout — Itemized (line-item tables) vs Scope-Centric
          (narrative / lump-sum "word style"). Affects the client-facing
          proposal render; the builder inputs stay the same either way. */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Document type
          </Label>
          <div
            className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2"
            role="radiogroup"
            aria-label="Document type"
            data-testid="estimate-layout-toggle"
          >
            {[
              {
                value: 'itemized',
                label: 'Line-Itemized',
                help: 'Qty / unit / rate tables',
                icon: LayoutList,
              },
              {
                value: 'scope',
                label: 'Scope-Centric',
                help: 'Narrative, lump-sum',
                icon: AlignLeft,
              },
              {
                value: 'tm',
                label: 'Time & Material',
                help: 'Rate-based agreement',
                icon: Clock,
              },
            ].map((opt) => {
              const active = (estimate?.layout || 'itemized') === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => onChange({ ...estimate, layout: opt.value })}
                  className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-left transition-colors ${
                    active
                      ? 'border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10'
                      : 'border-border bg-[hsl(var(--muted))] hover:border-[hsl(var(--primary))]/30'
                  }`}
                  data-testid={`estimate-layout-${opt.value}`}
                >
                  <Icon
                    size={16}
                    className={`mt-0.5 shrink-0 ${
                      active
                        ? 'text-[hsl(var(--primary))]'
                        : 'text-muted-foreground'
                    }`}
                  />
                  <span className="min-w-0">
                    <span
                      className={`block text-sm font-semibold ${
                        active ? 'text-foreground' : 'text-foreground/80'
                      }`}
                    >
                      {opt.label}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      {opt.help}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          {(estimate?.layout || 'itemized') === 'scope' ? (
            <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">
              Scope-centric groups sections into{' '}
              <span className="text-foreground/80 font-medium">Base Scope of Work</span> and{' '}
              <span className="text-foreground/80 font-medium">Allowance / Variable Scope</span>{' '}
              (set each section&apos;s type below) and shows line items as written
              bullets instead of priced tables.
            </p>
          ) : null}
          {estimate?.layout === 'tm' ? (
            <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">
              Time &amp; Material produces a rate-based agreement (deposit,
              hourly rate, material markup) instead of a priced scope. Line-item
              sections and tax are hidden.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* Time & Material terms (layout === 'tm'). */}
      {estimate?.layout === 'tm'
        ? (() => {
            const tm = estimate?.tm_terms || {};
            const setTm = (patch) =>
              onChange({
                ...estimate,
                tm_terms: { ...tm, ...patch },
              });
            return (
              <Card
                className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
                data-testid="estimate-tm-terms-card"
              >
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center gap-2 border-b border-border pb-3">
                    <Clock size={16} className="text-[hsl(var(--primary))]" />
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
                      Time &amp; Material terms
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                        Deposit / Retainer
                      </Label>
                      <Input
                        value={tm.deposit || ''}
                        onChange={(e) => setTm({ deposit: e.target.value })}
                        placeholder="4,500"
                        className="h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                        data-testid="tm-input-deposit"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                        Hourly rate
                      </Label>
                      <Input
                        value={tm.hourly_rate || ''}
                        onChange={(e) => setTm({ hourly_rate: e.target.value })}
                        placeholder="120"
                        className="h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                        data-testid="tm-input-rate"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                        Material markup %
                      </Label>
                      <Input
                        value={tm.material_markup_pct || ''}
                        onChange={(e) =>
                          setTm({ material_markup_pct: e.target.value })
                        }
                        placeholder="25"
                        className="h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                        data-testid="tm-input-markup"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                        Start date
                      </Label>
                      <Input
                        value={tm.start_date || ''}
                        onChange={(e) => setTm({ start_date: e.target.value })}
                        placeholder="TBD"
                        className="h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                        data-testid="tm-input-start"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                        Finish date
                      </Label>
                      <Input
                        value={tm.finish_date || ''}
                        onChange={(e) => setTm({ finish_date: e.target.value })}
                        placeholder="TBD"
                        className="h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                        data-testid="tm-input-finish"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      Record of hours &amp; receipts
                    </Label>
                    <Textarea
                      value={tm.submission_terms || ''}
                      onChange={(e) =>
                        setTm({ submission_terms: e.target.value })
                      }
                      className="min-h-[60px] bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="tm-input-submission"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      Payment terms
                    </Label>
                    <Textarea
                      value={tm.payment_terms || ''}
                      onChange={(e) => setTm({ payment_terms: e.target.value })}
                      className="min-h-[60px] bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="tm-input-payment"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      Additional notes
                    </Label>
                    <Textarea
                      value={tm.notes || ''}
                      onChange={(e) => setTm({ notes: e.target.value })}
                      placeholder="Anything else covered by this agreement…"
                      className="min-h-[60px] bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="tm-input-notes"
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })()
        : null}

      {/* Scope of Work — T&M agreements get the same open-doc block editor the
          Scope layout uses (headings, paragraphs, bullets, callouts…), just
          without priced tables/tax. Renders in the agreement above the terms. */}
      {estimate?.layout === 'tm' ? (
        <Card
          className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
          data-testid="estimate-tm-scope-card"
        >
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between gap-2 border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <ClipboardList size={16} className="text-[hsl(var(--primary))]" />
                <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
                  {t('estimates.tm.scope_title', { defaultValue: 'Scope of Work' })}
                </h3>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onAddSection}
                className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.14em] text-[11px]"
                data-testid="tm-add-scope-section"
              >
                <Plus size={13} className="mr-1.5" />
                {t('estimates.tm.add_section', { defaultValue: 'Add section' })}
              </Button>
            </div>
            {(estimate.sections || []).length === 0 ? (
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {t('estimates.tm.scope_hint', {
                  defaultValue:
                    'Describe the work this agreement covers — add a section, then write it like a document (headings, paragraphs, bullets, callouts). It prints above the T&M terms.',
                })}
              </p>
            ) : null}
            {(estimate.sections || []).map((section) => (
              <div
                key={section.id}
                className="rounded-md border border-border p-3 space-y-3"
                data-testid={`tm-scope-section-${section.id}`}
              >
                <div className="flex items-center gap-2">
                  <Input
                    value={section.name || ''}
                    onChange={(e) =>
                      onUpdateSection(section.id, { name: e.target.value })
                    }
                    placeholder={t('estimates.tm.section_name_placeholder', {
                      defaultValue: 'Section name (e.g. Scope of Work)',
                    })}
                    className="h-9 bg-[hsl(var(--muted))] border-border text-foreground font-medium"
                    data-testid={`tm-scope-name-${section.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveSection(section.id)}
                    className="text-muted-foreground hover:text-red-500 shrink-0"
                    data-testid={`tm-scope-remove-${section.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <SectionBlocksEditor
                  blocks={section.blocks}
                  onAdd={(type) => onAddBlock(section.id, type)}
                  onUpdate={(blockId, patch) =>
                    onUpdateBlock(section.id, blockId, patch)
                  }
                  onRemove={(blockId) => onRemoveBlock(section.id, blockId)}
                  onMove={(blockId, dir) => onMoveBlock(section.id, blockId, dir)}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* Contract pages — acceptance / signature (+ optional state legal). */}
      {(() => {
        const stateCode = resolveLegalState(estimate, null);
        const pack = getLegalPack(stateCode);
        return (
          <Card
            className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
            data-testid="estimate-contract-pages-card"
          >
            <CardContent className="p-4 space-y-3">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Contract pages
              </Label>

              {/* Acceptance / signature page */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    Acceptance &amp; signature page
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Appends a summary + owner / contractor signature lines,
                    ready to print &amp; sign.
                  </div>
                </div>
                <Switch
                  checked={!!estimate?.include_acceptance}
                  onCheckedChange={(v) =>
                    onChange({ ...estimate, include_acceptance: !!v })
                  }
                  data-testid="estimate-include-acceptance"
                />
              </div>

              {/* Legal add-on */}
              <div className="flex items-start justify-between gap-3 border-t border-border pt-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    Contract terms &amp; cancellation notice
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Adds Terms &amp; Conditions and the state cancellation-rights
                    appendix.
                  </div>
                </div>
                <Switch
                  checked={!!estimate?.include_legal}
                  onCheckedChange={(v) =>
                    onChange({ ...estimate, include_legal: !!v })
                  }
                  data-testid="estimate-include-legal"
                  disabled={!estimate?.include_acceptance}
                />
              </div>

              {/* State pack status (only relevant when legal is on) */}
              {estimate?.include_legal ? (
                pack ? (
                  <div className="text-[11px] text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.06] border border-[hsl(var(--primary))]/25 rounded px-3 py-2">
                    Using the <span className="font-semibold">{pack.state}</span>{' '}
                    legal pack (from the project address state).
                  </div>
                ) : (
                  <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded px-3 py-2">
                    No verified legal pack for{' '}
                    <span className="font-semibold">
                      {stateCode || 'this state'}
                    </span>
                    . Set the project address state to a configured state
                    (currently Illinois) — otherwise the proposal prints a
                    placeholder instead of legal text.
                  </div>
                )
              ) : null}

              {!estimate?.include_acceptance ? (
                <p className="text-[11px] text-muted-foreground/70">
                  Turn on the acceptance page to enable the legal add-on.
                </p>
              ) : null}
            </CardContent>
          </Card>
        );
      })()}

      {/* Overview + priced sections + tax — proposal layouts only (hidden for
          Time & Material, which is rate-based). */}
      {estimate?.layout !== 'tm' ? (
      <>
      {/* Project Overview */}
      <Card
        className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
        data-testid="estimate-overview-card"
      >
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-2 border-b border-border pb-3">
            <ClipboardList size={16} className="text-[hsl(var(--primary))]" />
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              {t('estimates.overview.title')}
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2 sm:col-span-2">
              <Label
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
                htmlFor="estimate-overview-description"
              >
                {t('estimates.overview.description')}
              </Label>
              <Textarea
                id="estimate-overview-description"
                rows={3}
                value={estimate?.overview?.description || ''}
                onChange={(e) =>
                  onChange({
                    ...estimate,
                    overview: {
                      ...(estimate.overview || {}),
                      description: e.target.value,
                    },
                  })
                }
                placeholder={t('estimates.builder.description_placeholder')}
                className="bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
                data-testid="estimate-overview-description"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
                htmlFor="estimate-overview-scope"
              >
                {t('estimates.overview.scope_summary')}
              </Label>
              <Textarea
                id="estimate-overview-scope"
                rows={3}
                value={estimate?.overview?.scope_summary || ''}
                onChange={(e) =>
                  onChange({
                    ...estimate,
                    overview: {
                      ...(estimate.overview || {}),
                      scope_summary: e.target.value,
                    },
                  })
                }
                placeholder={t('estimates.builder.scope_placeholder')}
                className="bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
                data-testid="estimate-overview-scope"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5"
                htmlFor="estimate-overview-address"
              >
                <MapPin size={11} />
                {t('estimates.overview.address')}
              </Label>
              <Input
                id="estimate-overview-address"
                value={estimate?.overview?.address || ''}
                onChange={(e) =>
                  onChange({
                    ...estimate,
                    overview: {
                      ...(estimate.overview || {}),
                      address: e.target.value,
                    },
                  })
                }
                placeholder={t('estimates.builder.address_placeholder')}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="estimate-overview-address"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label
                  className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5"
                  htmlFor="estimate-overview-est-start"
                >
                  <CalendarIcon size={11} />
                  {t('estimates.overview.est_start')}
                </Label>
                <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="accent-[hsl(var(--primary))]"
                    checked={estimate?.overview?.est_start === 'TBD'}
                    onChange={(e) =>
                      onChange({
                        ...estimate,
                        overview: {
                          ...(estimate.overview || {}),
                          est_start: e.target.checked ? 'TBD' : '',
                        },
                      })
                    }
                    data-testid="estimate-overview-est-start-tbd"
                  />
                  TBD
                </label>
              </div>
              <Input
                id="estimate-overview-est-start"
                type={estimate?.overview?.est_start === 'TBD' ? 'text' : 'date'}
                value={estimate?.overview?.est_start || ''}
                readOnly={estimate?.overview?.est_start === 'TBD'}
                onChange={(e) =>
                  onChange({
                    ...estimate,
                    overview: {
                      ...(estimate.overview || {}),
                      est_start: e.target.value,
                    },
                  })
                }
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="estimate-overview-est-start"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5"
                htmlFor="estimate-overview-conditions"
              >
                <AlertTriangle size={11} />
                {t('estimates.overview.special_conditions')}
              </Label>
              <Textarea
                id="estimate-overview-conditions"
                rows={3}
                value={estimate?.overview?.special_conditions || ''}
                onChange={(e) =>
                  onChange({
                    ...estimate,
                    overview: {
                      ...(estimate.overview || {}),
                      special_conditions: e.target.value,
                    },
                  })
                }
                placeholder={t('estimates.builder.conditions_placeholder')}
                className="bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
                data-testid="estimate-overview-conditions"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Proposal Terms card — Phase 26: surfaces the 9 columns added by 012.
          Reusable clauses (warranty / special_clauses / change_order_clause)
          are pre-filled from company defaults on new estimates by
          buildBlankEstimate(); editing here is bid-locked to this row. */}
      <Card
        className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
        data-testid="estimate-proposal-terms-card"
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-sm uppercase tracking-[0.18em] font-semibold text-muted-foreground flex items-center gap-2">
            <FileSignature size={14} className="text-[hsl(var(--primary))]" />
            Proposal terms
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground" htmlFor="est-prepared-by">
                Prepared by
              </Label>
              <Input
                id="est-prepared-by"
                value={estimate?.prepared_by || ''}
                onChange={(e) => onChange({ ...estimate, prepared_by: e.target.value })}
                placeholder="Your name"
                className="bg-[hsl(var(--muted))] border-border text-sm"
                data-testid="estimate-prepared-by"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground" htmlFor="est-valid-for">
                Proposal valid for
              </Label>
              <Input
                id="est-valid-for"
                value={estimate?.proposal_valid_for || ''}
                onChange={(e) => onChange({ ...estimate, proposal_valid_for: e.target.value })}
                placeholder="30 days"
                className="bg-[hsl(var(--muted))] border-border text-sm"
                data-testid="estimate-proposal-valid-for"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground" htmlFor="est-duration">
                Estimated duration
              </Label>
              <Input
                id="est-duration"
                value={estimate?.estimated_duration || ''}
                onChange={(e) => onChange({ ...estimate, estimated_duration: e.target.value })}
                placeholder="6–8 weeks"
                className="bg-[hsl(var(--muted))] border-border text-sm"
                data-testid="estimate-estimated-duration"
              />
            </div>
          </div>
          {[
            { key: 'exclusions',               label: 'Exclusions',               placeholder: 'What is NOT included in this scope...' },
            { key: 'client_responsibilities',  label: 'Client responsibilities',  placeholder: 'What the client needs to provide / decide / coordinate...' },
            { key: 'company_responsibilities', label: 'Company responsibilities', placeholder: 'What K.O. CO+RE will handle / coordinate / supply...' },
            { key: 'warranty',                 label: 'Warranty',                 placeholder: 'Pre-filled from company default; edit per-bid as needed.' },
            { key: 'special_clauses',          label: 'Special clauses',          placeholder: 'Pre-filled from company default; edit per-bid as needed.' },
            { key: 'change_order_clause',      label: 'Change-order clause',      placeholder: 'Pre-filled from company default; edit per-bid as needed.' },
          ].map((f) => (
            <div key={f.key} className="space-y-2">
              <Label
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
                htmlFor={`est-${f.key}`}
              >
                {f.label}
              </Label>
              <Textarea
                id={`est-${f.key}`}
                rows={3}
                value={estimate?.[f.key] || ''}
                onChange={(e) => onChange({ ...estimate, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
                data-testid={`estimate-${f.key.replace(/_/g, '-')}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Global Bottom Line toggle bar */}
      <Card
        className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
        data-testid="estimate-global-bottom-line-bar"
      >
        <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Lock
              size={14}
              className={
                anyBottomLine
                  ? 'text-[hsl(var(--primary))]'
                  : 'text-muted-foreground'
              }
            />
            <span className="text-xs sm:text-sm uppercase tracking-[0.18em] font-semibold text-foreground">
              {t('estimates.section.bottom_line_all')}
            </span>
            <HelpDot
              text={t('estimates.builder.bottom_line_tooltip')}
              label={t('estimates.builder.about_bottom_line')}
            />
            {anyBottomLine && !allBottomLine ? (
              <Badge
                variant="outline"
                className="bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30 text-[10px] uppercase tracking-[0.16em]"
              >
                {t('estimates.builder.partial_count', {
                  done: totals?.bottom_line_section_count,
                  total: totals?.section_count,
                })}
              </Badge>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              {allBottomLine
                ? t('estimates.builder.on')
                : t('estimates.builder.off')}
            </span>
            <Switch
              checked={allBottomLine}
              onCheckedChange={(v) => onSetAllSectionsBottomLineMode(!!v)}
              data-testid="estimate-global-bottom-line-toggle"
              className="data-[state=checked]:bg-[hsl(var(--primary))]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      {(estimate.sections || []).map((section) => (
        <SectionEditor
          key={section.id}
          section={section}
          layout={estimate?.layout || 'itemized'}
          onUpdate={onUpdateSection}
          onRemove={onRemoveSection}
          onAddBlock={onAddBlock}
          onUpdateBlock={onUpdateBlock}
          onRemoveBlock={onRemoveBlock}
          onMoveBlock={onMoveBlock}
          onAddItem={onAddItem}
          onUpdateItem={onUpdateItem}
          onRemoveItem={onRemoveItem}
          onAddMaterial={onAddMaterial}
          onUpdateMaterial={onUpdateMaterial}
          onRemoveMaterial={onRemoveMaterial}
          onAddAllowance={onAddAllowance}
          onUpdateAllowance={onUpdateAllowance}
          onRemoveAllowance={onRemoveAllowance}
          onSetBottomLineMode={onSetBottomLineMode}
          onSetBottomLineTotal={onSetBottomLineTotal}
        />
      ))}

      {/* Add section + tax rate */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Button
          size="sm"
          variant="outline"
          onClick={onAddSection}
          className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
          data-testid="estimate-add-section"
        >
          <Plus size={14} className="mr-1.5" />
          {t('estimates.builder.add_section')}
        </Button>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            {t('estimates.builder.tax_rate')}
          </span>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.25"
            value={estimate.tax_rate ?? 0}
            onChange={(e) =>
              onChange({ ...estimate, tax_rate: Number(e.target.value) || 0 })
            }
            className="h-9 w-24 bg-[hsl(var(--muted))] border-border text-foreground"
            data-testid="estimate-tax-rate"
          />
          <span className="text-muted-foreground">%</span>
        </div>
      </div>
      </>
      ) : null}

      {/* Internal notes */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {t('estimates.builder.internal_notes')}
          </Label>
          <Textarea
            rows={2}
            value={estimate.notes_text || ''}
            onChange={setField('notes_text')}
            placeholder={t('estimates.builder.internal_notes_placeholder')}
            className="mt-2 bg-[hsl(var(--muted))] border-border text-foreground resize-none"
            data-testid="estimate-notes"
          />
        </CardContent>
      </Card>

      {/* Estimate totals strip */}
      <Card
        className="border-[hsl(var(--primary))]/30 bg-[hsl(var(--card))]"
        data-testid="estimate-totals-strip"
      >
        <CardContent className="p-4 space-y-3">
          {/* HOP + Allowances informational */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-md border border-blue-400/20 bg-blue-500/5 p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('estimates.builder.hop_card_label')}
              </div>
              <div
                className="mt-1 text-base font-semibold text-foreground"
                data-testid="estimate-hop-total"
              >
                {formatUsd(totals?.hop_total)}
              </div>
              <div className="text-[10px] text-blue-700/70 dark:text-blue-300/70 mt-0.5">
                {t('estimates.builder.hop_card_help')}
              </div>
            </div>
            <div className="rounded-md border border-amber-400/30 bg-amber-500/5 p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5">
                <Wallet size={11} />
                {t('estimates.builder.allowances_card_label')}
              </div>
              <div
                className="mt-1 text-base font-semibold text-foreground"
                data-testid="estimate-allowances-total"
              >
                {formatUsd(totals?.allowances_total)}
              </div>
              <div className="text-[10px] text-amber-800/70 dark:text-amber-200/70 mt-0.5">
                {t('estimates.builder.allowances_card_help')}
              </div>
            </div>
          </div>

          {/* Subtotal / Tax / Total */}
          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border/60">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('estimates.totals.subtotal')}
              </div>
              <div
                className="mt-1 text-base font-semibold text-foreground"
                data-testid="estimate-subtotal"
              >
                {formatUsd(totals?.subtotal)}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {t('estimates.builder.subtotal_hint')}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('estimates.totals.tax')}
              </div>
              <div
                className="mt-1 text-base font-semibold text-foreground"
                data-testid="estimate-tax"
              >
                {formatUsd(totals?.tax)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('estimates.totals.total')}
              </div>
              <div
                className="mt-1 text-lg font-semibold text-[hsl(var(--primary))]"
                data-testid="estimate-total"
              >
                {formatUsd(totals?.total)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EstimateBuilder;
